import { ref, computed, getCurrentInstance } from '@vue/composition-api'

import InvoicingService from '@services/InvoicingService'
import pdfMake from 'pdfmake/build/pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts'
import * as XLSX from 'xlsx'
import JSZip from 'jszip'
import { saveAs } from 'file-saver'
import logoUrl from '@assets/img/logo-lg--colored.png'

pdfMake.vfs = pdfFonts.pdfMake ? pdfFonts.pdfMake.vfs : pdfFonts.vfs

const INVOICE_RED = '#c00000'
const TABLE_HEADER_BG = '#cceeee'
const GRID_LINE = '#333333'
const LINK_BLUE = '#1155cc'

// Fetched once and cached — the logo PNG is bundled as a webpack asset
// (a URL, not inline base64), but pdfmake's `image` field needs an actual
// base64 data URI, so it's fetched and converted on first use.
let cachedLogoDataUri = null
const getLogoDataUri = async () => {
  if (cachedLogoDataUri) return cachedLogoDataUri
  const resp = await fetch(logoUrl)
  const blob = await resp.blob()
  cachedLogoDataUri = await new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
  return cachedLogoDataUri
}

const LIMEIQ_COMPANY = {
  name: process.env.VUE_APP_INVOICE_COMPANY_NAME,
  addressLine: process.env.VUE_APP_INVOICE_COMPANY_ADDRESS,
  email: process.env.VUE_APP_INVOICE_COMPANY_EMAIL,
  fein: process.env.VUE_APP_INVOICE_COMPANY_FEIN,
}

const REMITTANCE_INSTRUCTIONS = {
  achWire: {
    accountHolder: process.env.VUE_APP_INVOICE_ACH_ACCOUNT_HOLDER,
    bank: process.env.VUE_APP_INVOICE_ACH_BANK,
    aba: process.env.VUE_APP_INVOICE_ACH_ABA,
    account: process.env.VUE_APP_INVOICE_ACH_ACCOUNT,
    swift: process.env.VUE_APP_INVOICE_ACH_SWIFT,
  },
  check: {
    payee: process.env.VUE_APP_INVOICE_CHECK_PAYEE,
    addressLines: [
      process.env.VUE_APP_INVOICE_CHECK_ADDRESS_LINE1,
      process.env.VUE_APP_INVOICE_CHECK_ADDRESS_LINE2,
    ].filter(Boolean),
  },
}

// Vitesse bulk-payment export column order — one row per net-payable
// InvoiceCharge line (see buildDraftXlsxWorkbook below).
const VITESSE_COLUMNS = [
  'LimeIQ Invoice #', 'Report Period', 'DCA', 'LimeIQ Key', 'Vitesse Account Name',
  'SendCurrency', 'RecipientName', 'RecipientCurrency', 'SendValue', 'ReceiveValue',
  'ReceipientReference', 'AccountIban', 'AccountNumber', 'AccountSortCode',
  'AccountSwift', 'AccountAbaCode', 'AccountBankName',
  'X-ClaimantName', 'X-CheckDate', 'X-Adjuster', 'X-DateofLoss', 'X-ServiceDates',
  'X-PayeeName', 'X-Comments', 'X-PersonalMessage',
]

const PAYMENT_TERMS_DAYS = Number(process.env.VUE_APP_INVOICE_PAYMENT_TERMS_DAYS) || 30

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

// "6/30/2026" — matches the invoice template's date format exactly.
const formatDateShort = (date) => `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`

// The invoice date is the UTC day the invoice was approved - the same date
// the dispatch email prints (LimeIQ reads Lincoln's approved_at, a UTC date).
// Built and formatted in UTC so the browser's timezone can't move it a day:
// approving at 8:42 PM CT is already the next day in UTC. Without an
// approved_at (a draft preview, or a reapprove, which restamps it now) it is
// today in UTC, which is what approving now would give.
const invoiceDateUtc = (approvedAt) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(approvedAt || '')
  if (match) return new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])))
  const now = new Date()
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()))
}
const formatUtcDateShort = (date) => `${date.getUTCMonth() + 1}/${date.getUTCDate()}/${date.getUTCFullYear()}`

// "May-2026"
const monthYearAbbrev = (month, year) => {
  const idx = Number(month) - 1
  const monAbbrev = MONTH_NAMES[idx] ? MONTH_NAMES[idx].slice(0, 3) : month
  return `${monAbbrev}-${year}`
}

// "LimeIQ-Mar-2026" — the Vitesse ReceipientReference, built from the
// invoice's own billing period (not today's date).
const recipientReference = (month, year) => `${LIMEIQ_COMPANY.name}-${monthYearAbbrev(month, year)}`

// Saves an axios blob response (responseType: 'blob') to disk, naming the
// file from the response's own Content-Disposition header when present
// (both InvoicingService.exportApprovedInvoicesXlsx and
// exportInvoiceChargesXlsx set one server-side) and falling back to
// fallbackName otherwise.
export const saveBlobResponse = (resp, fallbackName) => {
  const disposition = resp.headers && resp.headers['content-disposition']
  const match = disposition && disposition.match(/filename="?([^"]+)"?/)
  saveAs(resp.data, (match && match[1]) || fallbackName)
}

// Pure formatting helper, usable outside a component's setup() (e.g. the
// GenerateInvoicesTab.vue summary cards) as well as inside the composable.
// A charge's reporting period ('2026-08-31') as month and year ('Aug 2026'),
// like the Submission Period column (LIQ-2656). Read straight from the
// string - new Date('2026-08-31') is UTC midnight and can land on another
// day (or month) in the browser's timezone.
const REPORT_PERIOD_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
export const formatReportPeriod = (value) => {
  const match = /^(\d{4})-(\d{2})/.exec(value || '')
  if (!match) return value || ''
  return `${REPORT_PERIOD_MONTHS[Number(match[2]) - 1]} ${match[1]}`
}

export const formatCurrency = (val, currencyType) => {
  if (val === null || val === undefined) return '—'

  const isNegative = val < 0
  const absVal = Math.abs(val)

  const formattedNumber = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(absVal)

  // Only GBP and EUR get their native symbol — AUD/CAD/USD all
  // just show a plain $ sign to avoid ambiguous A$/CA$ prefixes.
  const symbolMap = {
    GBP: '£',
    EUR: '€',
  }

  const symbol = symbolMap[currencyType] || '$'
  return `${isNegative ? '-' : ''}${symbol}${formattedNumber}`
}

// Shared PDF/XLS preview + bulk export machinery for the Pending and
// Approved/Dispatched invoice tables. Both tables render the exact same
// per-invoice document — only the "download immediately" vs "collect into
// a zip" behavior differs (previewDraft vs exportSelected).
export function useInvoiceExport() {
  const instance = getCurrentInstance()
  const proxy = instance.proxy

  const varianceClass = (alert) => (alert ? 'gi-td--alert' : '')

  // Drafts have no persisted reference yet, so one is computed client-side.
  // Approved/dispatched invoices already carry their own real, stored
  // invoice_reference (minted at approval time from this same function) —
  // always prefer that one directly rather than recomputing.
  const generateRef = (item) => item.invoice_reference || InvoicingService.generateReference(
    item.billing_year,
    item.billing_month,
    item.currency_type,
    item.tpa_name,
    item.tpa_company_id,
    item.underwriter_name,
    item.syndicate_number,
  )

  // billing_contact comes from the API as an array of emails. Normalize
  // here in case any row ever comes through as null/undefined/a string.
  const billingContactList = (item) => {
    const val = item && item.billing_contact
    if (Array.isArray(val)) return val
    if (typeof val === 'string' && val) return [val]
    return []
  }

  // Display names for the same contacts, index-aligned with billingContactList
  // (LIQ-2158 — the page shows names, dispatch still sends to the emails).
  // Falls back to the email wherever the API sent no name.
  const billingContactNames = (item) => {
    const names = (item && Array.isArray(item.billing_contact_names)) ? item.billing_contact_names : []
    return billingContactList(item).map((email, idx) => names[idx] || email)
  }

  // Names and emails together — what the column filter / search box match on,
  // so a user can still find a row by either.
  const billingContactSearchText = (item) =>
    [...billingContactNames(item), ...billingContactList(item)].join(' ')

  // ── Billing contacts modal ───────────────────────────────────────────

  // Open/close state + the row the popup is for; the list itself is fetched
  // by BillingContactsModal.vue from the LIQ-2709 endpoint (LIQ-2710).
  const showBillingContactsModal = ref(false)
  const billingContactsModalContext = ref(null)

  const openBillingContactsModal = (item) => {
    billingContactsModalContext.value = item
    showBillingContactsModal.value = true
  }

  const onBillingContactsModalHide = () => {
    showBillingContactsModal.value = false
    billingContactsModalContext.value = null
  }

  // ── Single-draft preview/export ─────────────────────────────────────
  // fetchCharges + buildDraftPdfDocDefinition/buildDraftXlsxWorkbook are
  // shared between the single-row preview buttons and the bulk export
  // flow below — both paths build the exact same document, the only
  // difference is whether it's downloaded immediately (single) or
  // collected into a zip (bulk). Also shared between the Pending table
  // (still-unlinked draft groups) and the Approved table (real, persisted
  // invoices) — an approved row always carries invoice_id, a draft never
  // does, so that's what picks the right detail endpoint.
  const fetchCharges = async (item) => {
    const resp = item.invoice_id
      ? await InvoicingService.getInvoiceDetail({ invoice_id: item.invoice_id })
      : await InvoicingService.getDraftInvoiceDetail({
          tpa_company_id: item.tpa_company_id,
          underwriter_company_id: item.underwriter_company_id,
          currency_type: item.currency_type,
          billing_month: item.billing_month,
          billing_year: item.billing_year,
          // Same group identity the approve call uses - without it the
          // preview and the document would show both schemes' charges.
          billing_type: item.billing_type,
          // A LIMOSS group is split by syndicate too - without this the
          // document would pull every syndicate's charges for this
          // underwriter and period. Always null for Market.
          syndicate_number: item.syndicate_number,
        })
    const inner = resp.data && resp.data.data
    const rows = inner && inner.data
    return Array.isArray(rows) ? rows : []
  }

  // A charge's net contribution to THIS document. The invoice-detail
  // endpoint computes this server-side (contribution) since a charge's
  // amount and credit can land on two different invoices there; the
  // draft-detail endpoint has no such ambiguity (a still-unlinked
  // charge's own credit always belongs to the same draft group), so it
  // falls back to the simpler amount/credit_amount/is_redirected_credit
  // computation when contribution isn't present.
  const netChargeAmount = (c) => {
    if (typeof c.contribution === 'number') return c.contribution
    return c.is_redirected_credit ? -(c.credit_amount || 0) : (c.amount || 0) - (c.credit_amount || 0)
  }

  // Per-reporting-period fee breakdown for this invoice's own charges —
  // e.g. one row for "Apr-2026" and one for "May-2026" if the group spans
  // more than one underlying reporting_period (a late-arriving prior-month
  // file processed in this run). Falls back to the draft's own assigned
  // billing period if no charge carries a reporting_period. `date` mirrors
  // the invoice-level convention of "first of the month following the
  // reporting period" (when BDX ingestion/validation for that period
  // actually happens), computed per-period rather than once for the whole
  // invoice.
  const groupedReportPeriodAmounts = (draft, charges) => {
    const groups = new Map() // 'YYYY-MM' -> amount
    charges.forEach(c => {
      const key = c.reporting_period
        ? c.reporting_period.slice(0, 7)
        : `${draft.billing_year}-${String(draft.billing_month).padStart(2, '0')}`
      groups.set(key, (groups.get(key) || 0) + netChargeAmount(c))
    })
    return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([key, amount]) => {
      const [year, month] = key.split('-')
      return {
        period: monthYearAbbrev(month, year),
        date: new Date(Number(year), Number(month), 1),
        amount,
      }
    })
  }

  // label:value row for the remittance block, e.g. "Account Holder:  LimeIQ LLC"
  const labelValueRow = (label, value, marginTop = 2) => ({
    columns: [
      { width: 90, text: label, fontSize: 9, bold: true },
      { text: value || '—', fontSize: 9 },
    ],
    margin: [0, marginTop, 0, 0],
  })

  const buildDraftPdfDocDefinition = async (draft, charges) => {
    const ref_ = generateRef(draft)
    const ccy = draft.currency_type
    const reportPeriodAmounts = groupedReportPeriodAmounts(draft, charges)
    const logoDataUri = await getLogoDataUri()

    const invoiceDate = invoiceDateUtc(draft.approved_at)
    const dueDate = new Date(invoiceDate.getTime())
    dueDate.setUTCDate(dueDate.getUTCDate() + PAYMENT_TERMS_DAYS)

    // First of the month following the billing period — when BDX
    // ingestion/validation for that period actually happens.
    const lineDate = new Date(Number(draft.billing_year), Number(draft.billing_month), 1)


    const contactEmails = billingContactList(draft)
    const primaryContactEmail = contactEmails[0] || '—'

    // Invoice-level total only — per-BDX detail lives in the XLS export,
    // not here. Nets each charge against its own credit_amount, and nets
    // in any redirected-in credit from a different, already-dispatched
    // invoice (see is_redirected_credit) — computing from the charges
    // themselves, rather than trusting draft.current_amount, keeps this
    // total internally consistent even where the two aren't (yet) kept in
    // sync server-side.
    const computedTotal = charges.reduce((sum, c) => sum + netChargeAmount(c), 0)

    const companyAddressLines = (LIMEIQ_COMPANY.addressLine || '').split(',').map(s => s.trim()).filter(Boolean)
    // TPA's own address (Company.address, free-text) — not every TPA has
    // one on file, so this renders nothing when absent.
    const tpaAddressLines = (draft.tpa_address || '').split(',').map(s => s.trim()).filter(Boolean)

    return {
      ref_,
      docDefinition: {
        pageSize: 'A4',
        pageMargins: [40, 40, 40, 40],
        content: [
          // Centered logo + red INVOICE title.
          { image: logoDataUri, width: 200, alignment: 'center', margin: [0, 0, 0, 6] },
          { text: 'INVOICE', fontSize: 18, bold: true, color: INVOICE_RED, alignment: 'center', margin: [0, 0, 0, 20] },

          // Invoice meta — single left-aligned column.
          {
            stack: [
              { text: `Invoice Date:  ${formatUtcDateShort(invoiceDate)}`, fontSize: 9 },
              { text: `Invoice No.:  ${ref_}`, fontSize: 9, margin: [0, 2, 0, 0] },
              { text: `Currency:  ${ccy}`, fontSize: 9, margin: [0, 2, 0, 0] },
              { text: `Balance Due:  ${formatCurrency(computedTotal, ccy)}`, fontSize: 9, bold: true, margin: [0, 2, 0, 0] },
              { text: `FEIN: ${LIMEIQ_COMPANY.fein || '—'}`, fontSize: 9, margin: [0, 10, 0, 0] },
              { text: `Due Date:  ${formatUtcDateShort(dueDate)}`, fontSize: 9, bold: true, margin: [0, 10, 0, 0] },
            ],
            margin: [0, 0, 0, 20],
          },

          // TO / FROM — addressed to the Underwriter (the paying entity),
          // with the TPA shown as claims administrator on its behalf.
          {
            columns: [
              {
                width: '50%',
                stack: [
                  { text: 'TO', fontSize: 8, bold: true },
                  { text: draft.underwriter_name || '—', fontSize: 9, margin: [0, 4, 0, 0] },
                  { text: `c/o  ${draft.tpa_name || '—'} Claims Administration`, fontSize: 9, margin: [0, 8, 0, 0] },
                  ...tpaAddressLines.map(line => ({ text: line, fontSize: 9, margin: [0, 2, 0, 0] })),
                  { text: primaryContactEmail, fontSize: 9, margin: [0, 8, 0, 0] },
                ],
              },
              {
                width: '50%',
                stack: [
                  { text: 'FROM', fontSize: 8, bold: true },
                  { text: LIMEIQ_COMPANY.name, fontSize: 9, margin: [0, 4, 0, 0] },
                  ...companyAddressLines.map(line => ({ text: line, fontSize: 9, margin: [0, 2, 0, 0] })),
                  { text: LIMEIQ_COMPANY.email, fontSize: 9, color: LINK_BLUE, decoration: 'underline', margin: [0, 8, 0, 0] },
                ],
              },
            ],
            margin: [0, 0, 0, 24],
          },

          // One line per BDX reporting period represented in this invoice
          // (per-BDX detail is still only in the XLS export), each showing
          // that period's own fee on the right, plus TAX/TOTAL at the bottom.
          {
            table: {
              headerRows: 1,
              widths: [70, '*', 90],
              body: [
                [
                  { text: 'Date', fontSize: 9, bold: true, fillColor: TABLE_HEADER_BG, alignment: 'center' },
                  { text: 'Description', fontSize: 9, bold: true, fillColor: TABLE_HEADER_BG, alignment: 'center' },
                  { text: 'Fee', fontSize: 9, bold: true, fillColor: TABLE_HEADER_BG, alignment: 'center' },
                ],
                ...reportPeriodAmounts.map(({ period, date, amount }) => [
                  { text: formatDateShort(lineDate), fontSize: 9 },
                  { text: `BDX Ingestion, Validation and Export for Report Period ${period}`, fontSize: 9 },
                  { text: formatCurrency(amount, ccy), fontSize: 9, alignment: 'right' },
                ]),
                [
                  { text: '' },
                  { text: 'TAX', fontSize: 9, alignment: 'right' },
                  { text: formatCurrency(0, ccy), fontSize: 9, alignment: 'right' },
                ],
                [
                  { text: '' },
                  { text: 'TOTAL', bold: true, fontSize: 9, alignment: 'right' },
                  { text: formatCurrency(computedTotal, ccy), bold: true, fontSize: 9, alignment: 'right' },
                ],
              ],
            },
            layout: {
              hLineWidth: () => 0.5,
              vLineWidth: () => 0.5,
              hLineColor: () => GRID_LINE,
              vLineColor: () => GRID_LINE,
              paddingTop: () => 5,
              paddingBottom: () => 5,
              paddingLeft: () => 6,
              paddingRight: () => 6,
            },
            margin: [0, 0, 0, 20],
          },

          // Remittance Instructions — centered title, single centered
          // stacked block (ACH/Wire above Check), not two columns.
          { text: 'REMITTANCE INSTRUCTIONS', fontSize: 11, bold: true, alignment: 'center', margin: [0, 6, 0, 16] },
          {
            columns: [
              { width: '*', text: '' },
              {
                width: 230,
                stack: [
                  { text: 'ACH/Wire', fontSize: 9, bold: true, decoration: 'underline' },
                  labelValueRow('Account Holder:', REMITTANCE_INSTRUCTIONS.achWire.accountHolder, 4),
                  labelValueRow('Bank Name:', REMITTANCE_INSTRUCTIONS.achWire.bank),
                  labelValueRow('ABA #:', REMITTANCE_INSTRUCTIONS.achWire.aba),
                  labelValueRow('Account #:', REMITTANCE_INSTRUCTIONS.achWire.account),
                  labelValueRow('Swift:', REMITTANCE_INSTRUCTIONS.achWire.swift),
                  { text: 'Check', fontSize: 9, bold: true, decoration: 'underline', margin: [0, 16, 0, 0] },
                  labelValueRow('Payee:', REMITTANCE_INSTRUCTIONS.check.payee, 4),
                  {
                    columns: [
                      { width: 90, text: 'Address:', fontSize: 9, bold: true },
                      { stack: REMITTANCE_INSTRUCTIONS.check.addressLines.map(line => ({ text: line, fontSize: 9 })) },
                    ],
                    margin: [0, 2, 0, 0],
                  },
                ],
              },
              { width: '*', text: '' },
            ],
            margin: [0, 0, 0, 20],
          },

          { text: 'PLEASE REFERENCE LIMEIQ INVOICE NUMBER ON REMITTANCE', fontSize: 10, bold: true, alignment: 'center' },
        ],
        defaultStyle: { font: 'Roboto' },
      },
    }
  }

  // Vitesse bulk-payment export — one row per net-payable charge, fed
  // straight into the payment processor. A charge nets against its own
  // credit_amount; a redirected-in credit (from a different, already-
  // dispatched invoice — see is_redirected_credit) is its own negative
  // row. Rows that net to exactly zero are dropped — there's nothing to
  // actually pay.
  const buildDraftXlsxWorkbook = (draft, charges) => {
    const ref_ = generateRef(draft)
    const ccy = draft.currency_type
    const reference = recipientReference(draft.billing_month, draft.billing_year)

    const rows = []
    charges.forEach(c => {
      const netAmount = Math.round(netChargeAmount(c) * 100) / 100
      if (netAmount === 0) return
      const [reportYear, reportMonth] = c.reporting_period
        ? c.reporting_period.slice(0, 7).split('-')
        : [draft.billing_year, draft.billing_month]
      rows.push({
        'LimeIQ Invoice #': ref_,
        'Report Period': monthYearAbbrev(reportMonth, reportYear),
        'DCA': draft.tpa_name || '',
        'LimeIQ Key': c.bdx_key || '',
        'Vitesse Account Name': c.account_name || '',
        'SendCurrency': ccy || '',
        'RecipientName': LIMEIQ_COMPANY.name,
        'RecipientCurrency': 'USD', // Always USD, regardless of the invoice's own currency.
        'SendValue': netAmount,
        'ReceiveValue': netAmount,
        'ReceipientReference': reference,
        'AccountIban': '',
        'AccountNumber': REMITTANCE_INSTRUCTIONS.achWire.account,
        'AccountSortCode': '',
        'AccountSwift': REMITTANCE_INSTRUCTIONS.achWire.swift,
        'AccountAbaCode': REMITTANCE_INSTRUCTIONS.achWire.aba,
        'AccountBankName': REMITTANCE_INSTRUCTIONS.achWire.bank,
        'X-ClaimantName': '',
        'X-CheckDate': '',
        'X-Adjuster': '',
        'X-DateofLoss': '',
        'X-ServiceDates': '',
        'X-PayeeName': '',
        'X-Comments': '',
        'X-PersonalMessage': '',
      })
    })

    const ws = XLSX.utils.json_to_sheet(rows, { header: VITESSE_COLUMNS })
    ws['!cols'] = VITESSE_COLUMNS.map(() => ({ wch: 18 }))

    const wb = XLSX.utils.book_new()
    // Sheet name mirrors the invoice reference (Excel caps names at 31
    // chars — generateReference already keeps refs at 20, but clamp
    // defensively).
    XLSX.utils.book_append_sheet(wb, ws, ref_.slice(0, 31))

    return { ref_, workbook: wb }
  }

  // ── Stored-document download (Approved table) ───────────────────────
  // Approved-invoice documents are always fetched from Azure — never
  // recomputed from charges client-side. Throws if nothing is stored
  // (e.g. Lincoln/Azure unreachable, or an invoice approved before this
  // archival feature existed) — the caller decides how to surface that.
  const downloadStoredDocument = async (item, format) => {
    const docType = format === 'pdf' ? 'pdf' : 'xls'
    const ext = format === 'pdf' ? 'pdf' : 'xlsx'
    const resp = await InvoicingService.downloadInvoiceDocument({ invoice_id: item.invoice_id, doc_type: docType })
    saveAs(resp.data, `${generateRef(item)}.${ext}`)
  }

  // ── Generate + archive documents (called right after Approve/Reapprove) ──
  // The frontend already knows how to build both files (buildDraftPdfDocDefinition/
  // buildDraftXlsxWorkbook) — this just converts each to raw bytes and
  // uploads them for storage (see Lincoln's InvoiceDocumentUploadView).
  // Accepts a single item or an array (bulk approve produces several new
  // invoices at once); best-effort per item — one invoice's document
  // upload failing doesn't affect the others, and never blocks the
  // approval itself (which has already succeeded by the time this runs).

  const docsUploading = ref(false)
  const docsUploadTotal = ref(0)
  const docsUploadDone = ref(0)

  const uploadOneDocument = async (item, docType, bytes, filename, mimeType) => {
    const formData = new FormData()
    formData.append('invoice_id', item.invoice_id)
    formData.append('doc_type', docType)
    formData.append('file', new Blob([bytes], { type: mimeType }), filename)
    await InvoicingService.uploadInvoiceDocument(formData)
  }

  const generateAndUploadDocuments = async (items) => {
    const targets = (Array.isArray(items) ? items : [items]).filter(item => item && item.invoice_id)
    if (!targets.length) return

    docsUploading.value = true
    docsUploadTotal.value = targets.length
    docsUploadDone.value = 0

    const failures = []

    for (const item of targets) {
      try {
        const charges = await fetchCharges(item)

        const { ref_, docDefinition } = await buildDraftPdfDocDefinition(item, charges)
        const pdfBuffer = await pdfToArrayBuffer(docDefinition)
        await uploadOneDocument(item, 'pdf', pdfBuffer, `${ref_}.pdf`, 'application/pdf')

        const { workbook } = buildDraftXlsxWorkbook(item, charges)
        const xlsBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
        await uploadOneDocument(item, 'xls', xlsBuffer, `${ref_}.xlsx`, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')
      } catch (e) {
        console.error('Document generation/upload failed for invoice:', item, e)
        failures.push(item)
      } finally {
        docsUploadDone.value += 1
      }
    }

    docsUploading.value = false
    docsUploadTotal.value = 0
    docsUploadDone.value = 0

    if (failures.length) {
      proxy.$bvToast && proxy.$bvToast.toast(
        `${failures.length} invoice(s) approved, but their PDF/XLS failed to archive. You can still export them manually.`,
        { title: 'Partial failure', variant: 'warning', autoHideDelay: 6000 }
      )
    }
  }

  const previewDraft = async (draft, format) => {
    try {
      const charges = await fetchCharges(draft)

      if (format === 'pdf') {
        const { ref_, docDefinition } = await buildDraftPdfDocDefinition(draft, charges)
        pdfMake.createPdf(docDefinition).download(`${ref_}.pdf`)
      } else if (format === 'xls') {
        const { ref_, workbook } = buildDraftXlsxWorkbook(draft, charges)
        XLSX.writeFile(workbook, `${ref_}.xlsx`)
      }
    } catch (e) {
      console.error('Preview failed:', e)
      proxy.$bvToast && proxy.$bvToast.toast(
        'Failed to generate preview. Please try again.',
        { title: 'Error', variant: 'danger', autoHideDelay: 4000 }
      )
    }
  }

  // ── Bulk export (selected drafts, zipped) ───────────────────────────
  // Fetches charge detail + builds the doc for each selected draft one
  // at a time (sequential, not Promise.all) so the progress bar updates
  // smoothly and so we don't hammer the detail endpoint with N
  // simultaneous requests. Each generated file is added to a zip and a
  // single .zip is downloaded at the end.

  const bulkExporting = ref(false)
  const bulkExportFormat = ref(null)
  const bulkExportTotal = ref(0)
  const bulkExportDone = ref(0)
  const bulkExportPct = computed(() =>
    bulkExportTotal.value === 0 ? 0 : Math.round((bulkExportDone.value / bulkExportTotal.value) * 100))

  const pdfToArrayBuffer = (docDefinition) =>
    new Promise((resolve, reject) => {
      try {
        pdfMake.createPdf(docDefinition).getBuffer((buffer) => resolve(buffer))
      } catch (e) {
        reject(e)
      }
    })

  const exportSelected = async (targets, format) => {
    if (!targets.length) return

    bulkExporting.value = true
    bulkExportFormat.value = format
    bulkExportTotal.value = targets.length
    bulkExportDone.value = 0

    const zip = new JSZip()
    const usedNames = new Set()
    const errors = []

    const uniqueFileName = (base, ext) => {
      let name = `${base}.${ext}`
      let i = 2
      while (usedNames.has(name)) {
        name = `${base} (${i}).${ext}`
        i++
      }
      usedNames.add(name)
      return name
    }

    for (const draft of targets) {
      try {
        const charges = await fetchCharges(draft)

        if (format === 'pdf') {
          const { ref_, docDefinition } = await buildDraftPdfDocDefinition(draft, charges)
          const buffer = await pdfToArrayBuffer(docDefinition)
          zip.file(uniqueFileName(ref_, 'pdf'), buffer)
        } else if (format === 'xls') {
          const { ref_, workbook } = buildDraftXlsxWorkbook(draft, charges)
          const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
          zip.file(uniqueFileName(ref_, 'xlsx'), wbout)
        }
      } catch (e) {
        console.error('Bulk export failed for draft:', draft, e)
        errors.push(draft)
      } finally {
        bulkExportDone.value += 1
      }
    }

    try {
      const zipBlob = await zip.generateAsync({ type: 'blob' })
      const stamp = new Date().toISOString().slice(0, 10)
      saveAs(zipBlob, `invoices-${format}-${stamp}.zip`)

      if (errors.length) {
        proxy.$bvToast && proxy.$bvToast.toast(
          `${errors.length} of ${targets.length} invoice(s) failed to export. The rest are in the zip.`,
          { title: 'Partial export', variant: 'warning', autoHideDelay: 5000 }
        )
      } else {
        proxy.$bvToast && proxy.$bvToast.toast(
          `${targets.length} invoice(s) exported successfully.`,
          { title: 'Export complete', variant: 'success', autoHideDelay: 3000 }
        )
      }
    } catch (e) {
      console.error('Zip generation failed:', e)
      proxy.$bvToast && proxy.$bvToast.toast(
        'Failed to build the export zip. Please try again.',
        { title: 'Error', variant: 'danger', autoHideDelay: 4000 }
      )
    } finally {
      bulkExporting.value = false
      bulkExportFormat.value = null
      bulkExportTotal.value = 0
      bulkExportDone.value = 0
    }
  }

  return {
    formatCurrency,
    varianceClass,
    generateRef,
    billingContactList,
    billingContactNames,
    billingContactSearchText,

    showBillingContactsModal,
    billingContactsModalContext,
    openBillingContactsModal,
    onBillingContactsModalHide,

    fetchCharges,
    previewDraft,
    downloadStoredDocument,

    docsUploading,
    docsUploadTotal,
    docsUploadDone,
    generateAndUploadDocuments,

    bulkExporting,
    bulkExportFormat,
    bulkExportTotal,
    bulkExportDone,
    bulkExportPct,
    exportSelected,
  }
}
